import React, { useCallback, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import { TableRow, TableRowCell } from 'core-system/Table'
import Text from 'core-system/Text'
import Chip from 'core-system/Chip'
import { SelectItemProps } from 'core-system/Dropdown'
import ChevronDown from 'core-system/Icons/Actions/Chevron'
import Portal from 'core-system/Portal'
import palette from 'core-system/Themes/palette'
import { dashboardActions } from '../dashboardActions'
import {
  TransitException,
  TransitExceptionStatus,
  TransitExceptionType,
} from '../dashboardTypes'

const EXCEPTION_TYPE_LABELS: Record<TransitExceptionType, string> = {
  FAILED_CARD_UPLOAD: 'Failed card upload',
  INVALID_CARD_NUMBER: 'Invalid card number',
  ACTIVATION_WINDOW: 'Activation window',
}

const STATUS_OPTIONS: SelectItemProps[] = [
  { id: 'OPEN', text: 'Open' },
  { id: 'RESOLVED', text: 'Closed' },
]

const StatusControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
`

const StatusChevronButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  border: none;
  border-radius: 0.25rem;
  background: transparent;
  cursor: pointer;
  color: ${palette.text.secondary};
`

const StatusMenu = styled.div<{ top: number; left: number; width: number }>`
  position: fixed;
  z-index: ${(p) => p.theme.zIndex.dropdowns};
  top: ${(p) => p.top}px;
  left: ${(p) => p.left}px;
  width: ${(p) => p.width}px;
  padding: 0.5rem 0;
  border: 1px solid ${palette.grey.grey300};
  border-radius: 0.25rem;
  background-color: ${palette.white};
  box-shadow: ${(p) => p.theme.dropShadows.selected};
  overflow: hidden;
`

const StatusMenuItem = styled.div<{ active?: boolean }>`
  ${(p) => p.theme.typography.action4};
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 0.625rem 1rem;
  cursor: pointer;
  color: ${(p) =>
    p.active ? palette.secondary.purple600 : palette.text.primary};
  white-space: nowrap;

  &:hover {
    background-color: ${palette.secondary.purple200};
  }
`

const formatOpenDuration = (exception: TransitException) => {
  const start = moment(exception.createdAt)
  const end =
    exception.status === 'RESOLVED' && exception.resolvedAt
      ? moment(exception.resolvedAt)
      : moment()
  return start.from(end, true)
}

interface ExceptionQueuedRowProps {
  exception: TransitException
}

const ExceptionQueuedRow = ({ exception }: ExceptionQueuedRowProps) => {
  const dispatch = useDispatch()
  const chevronRef = useRef<HTMLButtonElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 120 })

  const typeLabel =
    EXCEPTION_TYPE_LABELS[exception.exceptionType] || exception.exceptionType

  const openMenu = useCallback(() => {
    const rect = chevronRef.current?.getBoundingClientRect()
    if (!rect) {
      return
    }
    setCoords({
      top: rect.bottom + 4,
      left: rect.right - 120,
      width: 120,
    })
    setIsOpen((prev) => !prev)
  }, [])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    const closeOnScroll = () => setIsOpen(false)
    window.addEventListener('scroll', closeOnScroll, true)
    return () => window.removeEventListener('scroll', closeOnScroll, true)
  }, [isOpen])

  const handleStatusChange = (status: TransitExceptionStatus) => {
    setIsOpen(false)
    if (status === exception.status) {
      return
    }
    dispatch(
      dashboardActions.updateDashboardTransitExceptionStatus({
        id: exception.id,
        status,
      })
    )
  }

  return (
    <TableRow data-cy='exception-queued-row'>
      <TableRowCell>
        <Text variant='action4'>{typeLabel}</Text>
      </TableRowCell>
      <TableRowCell>
        <Text variant='action3'>{exception.message}</Text>
      </TableRowCell>
      <TableRowCell>
        <Text variant='body2'>{exception.actionMessage}</Text>
      </TableRowCell>
      <TableRowCell>
        <Text variant='body2'>{formatOpenDuration(exception)}</Text>
      </TableRowCell>
      <TableRowCell>
        <StatusControl>
          <Chip
            variant={exception.status === 'OPEN' ? 'red' : 'grey'}
            isInteractive={false}
          >
            {exception.status === 'OPEN' ? 'Open' : 'Closed'}
          </Chip>
          <StatusChevronButton
            ref={chevronRef}
            type='button'
            aria-label='Change status'
            data-cy='exception-status-dropdown'
            onClick={openMenu}
          >
            <ChevronDown height={16} width={16} />
          </StatusChevronButton>
          {isOpen && (
            <Portal>
              <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                <StatusMenu
                  top={coords.top}
                  left={coords.left}
                  width={coords.width}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <StatusMenuItem
                      key={option.id}
                      active={option.id === exception.status}
                      onClick={() =>
                        handleStatusChange(option.id as TransitExceptionStatus)
                      }
                    >
                      {option.text}
                    </StatusMenuItem>
                  ))}
                </StatusMenu>
              </ClickAwayListener>
            </Portal>
          )}
        </StatusControl>
      </TableRowCell>
    </TableRow>
  )
}

export default React.memo(ExceptionQueuedRow)
